import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import EntityHero from '@/pages/entity/EntityHero';
import EntityHeader from '@/pages/entity/EntityHeader';
import EntityMainContent from '@/pages/entity/EntityMainContent';
import EntitySidebar from '@/pages/entity/EntitySidebar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EntityDetail = () => {
  const { entityId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [entityAdminEmails, setEntityAdminEmails] = useState(['', '']);
  const [entityAdminUsers, setEntityAdminUsers] = useState([]);

  const fetchEntity = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/entities/${entityId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setEntity(response.data);
    } catch (error) {
      console.error('Error fetching entity:', error);
      toast.error('Failed to load details');
      navigate('/discovery');
    } finally {
      setLoading(false);
    }
  }, [entityId, token, navigate]);

  const trackEntityView = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.post(`${API}/badges/track-action`, null, {
        params: { action: 'view_entity' },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      // Check if any badges were earned
      if (response.data.newly_earned_badges?.length > 0) {
        response.data.newly_earned_badges.forEach(badge => {
          toast.success(`Badge Unlocked: ${badge.name}!`, {
            description: badge.description,
            duration: 5000
          });
        });
      }
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  }, [token]);

  const fetchEntityAdminUsers = useCallback(async () => {
    if (!entity?.entity_admins || entity.entity_admins.length === 0) {
      setEntityAdminUsers([]);
      return;
    }
    
    try {
      const userPromises = entity.entity_admins.map(userId =>
        axios.get(`${API}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }).catch(() => null)
      );
      
      const responses = await Promise.all(userPromises);
      const users = responses.filter(r => r !== null).map(r => r.data);
      setEntityAdminUsers(users);
    } catch (error) {
      console.error('Error fetching entity admin users:', error);
    }
  }, [entity, token]);

  useEffect(() => {
    fetchEntity();
    trackEntityView();
  }, [fetchEntity, trackEntityView]);

  useEffect(() => {
    if (entity && entity.entity_admins) {
      fetchEntityAdminUsers();
    }
  }, [entity, fetchEntityAdminUsers]);

  const handleSaveEntityAdmins = async () => {
    const validEmails = entityAdminEmails.filter(email => email.trim() !== '');
    
    if (validEmails.length > 2) {
      toast.error('Maximum 2 partner admins allowed');
      return;
    }
    
    try {
      await axios.put(`${API}/admin/entities/${entityId}/admins`,
        { emails: validEmails },
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );
      toast.success('Partner admins updated successfully');
      fetchEntity();
      setEntityAdminEmails(['', '']);
    } catch (error) {
      console.error('Error updating entity admins:', error);
      toast.error(error.response?.data?.detail || 'Failed to update partner admins');
    }
  };

  const handleRemoveEntityAdmin = async (userId) => {
    const remainingAdmins = entity.entity_admins.filter(id => id !== userId);
    
    try {
      await axios.put(`${API}/admin/entities/${entityId}/admins`,
        { user_ids: remainingAdmins },
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );
      toast.success('Partner admin removed');
      fetchEntity();
    } catch (error) {
      console.error('Error removing entity admin:', error);
      toast.error('Failed to remove partner admin');
    }
  };

  const handleSave = async () => {
    const newSavedState = !saved;
    setSaved(newSavedState);
    toast.success(newSavedState ? 'Saved to favorites' : 'Removed from saved');
    
    // Track favorite for badge progress
    if (newSavedState && token) {
      try {
        const response = await axios.post(`${API}/badges/track-action`, null, {
          params: { action: 'save_favorite' },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        
        if (response.data.newly_earned_badges?.length > 0) {
          response.data.newly_earned_badges.forEach(badge => {
            toast.success(`Badge Unlocked: ${badge.name}!`, {
              description: badge.description,
              duration: 5000
            });
          });
        }
      } catch (error) {
        console.error('Error tracking favorite:', error);
      }
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard');
  };

  const handleConnect = () => {
    toast.info('Connection request feature coming soon!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-20 flex justify-center items-center min-h-[60vh]">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-20 flex flex-col justify-center items-center min-h-[60vh]">
          <p className="text-muted-foreground mb-4">Partner not found</p>
          <Button onClick={() => navigate('/discovery')}>Back to Discovery</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="entity-detail-page">
      <Navigation />
      
      <main className="pt-[60px] pb-8 sm:pt-20 sm:pb-12">
        <EntityHero
          entity={entity}
          saved={saved}
          onBack={() => navigate(-1)}
          onSave={handleSave}
          onShare={handleShare}
        />

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 -mt-12 sm:-mt-20 relative z-10">
          {/* Header Card */}
          <EntityHeader entity={entity} onConnect={handleConnect} />

          {/* Details Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Content */}
            <EntityMainContent entity={entity} currentUser={user} onAggregateChange={(agg) => setEntity((e) => (e ? { ...e, ...agg } : e))} />
            {/* Sidebar */}
            <EntitySidebar
              entity={entity}
              user={user}
              entityAdminUsers={entityAdminUsers}
              entityAdminEmails={entityAdminEmails}
              setEntityAdminEmails={setEntityAdminEmails}
              onSaveEntityAdmins={handleSaveEntityAdmins}
              onRemoveEntityAdmin={handleRemoveEntityAdmin}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default EntityDetail;
